import { useQueries, useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import type { Indicator } from '@/types/indicator';

// ── Types ─────────────────────────────────────────────────────────────────────

/** One territory entry as returned by GET /territories */
export interface TerritoryEntry {
  id: number;
  code_insee: string;
  name: string;
  type: string | null;
  population: number | null;
  surface_ha: number | null;
  has_full_data?: boolean;
}

/** Structured comparison metrics extracted from raw indicators. */
export interface CompareMetrics {
  densite: number | null;       // hab/km²
  sols_artificialises: number | null; // %
  canopee: number | null;       // %
  passoires: number | null;     // % logements F+G
  solaire: number | null;       // GWh/an
  croissance: number | null;    // % pop growth
}

/** Shape returned by useComparaisonIndicators */
export interface ComparaisonIndicatorResult {
  metrics: CompareMetrics;
  isLoading: boolean;
  error: Error | null;
}

/** All-null comparison metrics (no data available). */
function emptyMetrics(): CompareMetrics {
  return {
    densite: null,
    sols_artificialises: null,
    canopee: null,
    passoires: null,
    solaire: null,
    croissance: null,
  };
}

// ── Indicator name matchers ───────────────────────────────────────────────────

/** Extract a comparison metric from the raw indicators array. */
function extractMetrics(indicators: Indicator[]): CompareMetrics {
  // Le potentiel solaire est une ESTIMATION méthodologique (irradiation PVGIS/JRC
  // × surface de toiture BD TOPO × coefficients standard). Il est affiché — comme
  // sur l'écran Cadastre solaire — mais libellé « (est.) » côté UI et « estimé »
  // dans la lecture comparative. Cohérence assumée entre les deux écrans.
  const find = (patterns: string[]): number | null => {
    for (const ind of indicators) {
      const nameLow = ind.name.toLowerCase();
      if (patterns.some((p) => nameLow.includes(p.toLowerCase()))) {
        return ind.value ?? null;
      }
    }
    return null;
  };

  return {
    densite: find(['densit', 'densite', 'density']),
    sols_artificialises: find(['artificialisé', 'artificialise', 'artif']),
    canopee: find(['canopée', 'canopee', 'canopy', 'arbres', 'végétation']),
    // Indicateur réel : dpe_f_g_pct (underscores). Les anciens motifs « dpe f »
    // / « dpe g » (espaces) ne matchaient jamais → passoires toujours null.
    passoires: find(['passoire', 'dpe_f_g', 'f_g_pct', 'énergivore']),
    solaire: find(['solaire', 'photovoltaïque', 'pv', 'solar']),
    croissance: find(['croissance', 'évolution population', 'variation pop']),
  };
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

/** List all territories from the API. */
export function useComparisonTerritories() {
  return useQuery<TerritoryEntry[]>({
    queryKey: ['comparison-territories'],
    queryFn: async () => {
      const { data } = await api.get<TerritoryEntry[]>('/territories');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Fetch and structure comparison metrics for a single territory. */
export function useComparaisonIndicators(
  insee: string | null,
): ComparaisonIndicatorResult {
  const { data, isLoading, error } = useQuery<Indicator[]>({
    queryKey: ['indicators', insee],
    queryFn: async () => {
      const { data } = await api.get(`/territories/${insee}/indicators`);
      // API may return { indicators: [...] } or a plain array
      return Array.isArray(data) ? data : (data.indicators ?? []);
    },
    enabled: !!insee,
    staleTime: 5 * 60 * 1000,
  });

  return {
    metrics: data ? extractMetrics(data) : emptyMetrics(),
    isLoading,
    error: error as Error | null,
  };
}

/** Result of usePeersMetrics. */
export interface PeersMetricsResult {
  /** Structured metrics keyed by INSEE code. */
  byInsee: Record<string, CompareMetrics>;
  isLoading: boolean;
}

/**
 * Fetch and structure comparison metrics for several peer territories at once.
 * Uses useQueries so the number of hooks stays stable across renders.
 */
export function usePeersMetrics(insees: string[]): PeersMetricsResult {
  const results = useQueries({
    queries: insees.map((insee) => ({
      queryKey: ['indicators', insee],
      queryFn: async () => {
        const { data } = await api.get(`/territories/${insee}/indicators`);
        return (Array.isArray(data) ? data : (data.indicators ?? [])) as Indicator[];
      },
      staleTime: 5 * 60 * 1000,
    })),
  });

  const byInsee: Record<string, CompareMetrics> = {};
  insees.forEach((insee, i) => {
    const data = results[i]?.data;
    byInsee[insee] = data ? extractMetrics(data) : emptyMetrics();
  });

  return {
    byInsee,
    isLoading: results.some((r) => r.isLoading),
  };
}
